export type ResolvedAiApiError = {
  status: number;
  code: string | null;
  message: string;
  retryAfterSeconds?: number;
};

function readHeader(
  headers: Headers | Record<string, any> | undefined,
  name: string,
) {
  if (!headers) return null;
  if (typeof (headers as any).get === "function") {
    return (headers as Headers).get(name);
  }
  const key = Object.keys(headers).find(
    (k) => k.toLowerCase() === name.toLowerCase(),
  );
  return key ? String((headers as any)[key]) : null;
}

export function resolveAiApiError(opts: {
  status: number;
  body: any;
  headers?: Headers | Record<string, any>;
  t: (key: string, values?: Record<string, any>) => string;
  fallback: string;
}): ResolvedAiApiError {
  const code: string | null =
    (typeof opts.body?.error === "string" && opts.body.error) ||
    (typeof opts.body?.errorCode === "string" && opts.body.errorCode) ||
    (typeof opts.body?.code === "string" && opts.body.code) ||
    null;

  if (opts.status === 403 && code === "EMAIL_NOT_VERIFIED") {
    return { status: opts.status, code, message: opts.t("email_not_verified") };
  }

  if (opts.status === 429 && code === "RATE_LIMITED") {
    const retryAfterRaw = readHeader(opts.headers, "Retry-After");
    const seconds = Math.max(1, Number(retryAfterRaw) || 60);
    return {
      status: opts.status,
      code,
      message: opts.t("rate_limited", { seconds }),
      retryAfterSeconds: seconds,
    };
  }

  if (opts.status === 429 && code === "QUOTA_DAILY_CAP_EXCEEDED") {
    const dailyUsed = opts.body?.dailyUsed;
    const dailyCap = opts.body?.dailyCap;
    const hasUsage =
      typeof dailyUsed === "number" && typeof dailyCap === "number";
    return {
      status: opts.status,
      code,
      message: hasUsage
        ? opts.t("error.quota_daily_cap_exceeded_detail", {
            used: dailyUsed,
            cap: dailyCap,
          })
        : opts.t("error.quota_daily_cap_exceeded"),
    };
  }

  if (
    opts.status === 429 &&
    (code === "QUOTA_EXCEEDED" || code === "QUOTA_EXHAUSTED")
  ) {
    return {
      status: opts.status,
      code,
      message: opts.t("error.quota_exhausted"),
    };
  }

  if (code === "TECHNICAL_ISSUE") {
    return {
      status: opts.status,
      code,
      message: opts.t("error.technical_issue"),
    };
  }

  const message =
    (typeof opts.body?.message === "string" && opts.body.message) ||
    (typeof opts.body?.content === "string" && opts.body.content) ||
    (typeof opts.body?.error === "string" && opts.body.error) ||
    opts.fallback;

  return {
    status: opts.status,
    code,
    message,
  };
}
